'use client';
import type { Range } from '@tanstack/react-virtual';
import { defaultRangeExtractor, useVirtualizer } from '@tanstack/react-virtual';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { useAccountContext } from '~context/account';
import { usePaywallContext } from '~context/paywall';
import { generationModel } from '~entities/generation';
import { PaymentReason, PaymentReasonType } from '~features/annals';
import { annals } from '~features/annals/hooks';
import { loadFromSessionStorage, ssKeys } from '~features/storage';
import { useCurrentBot } from '~hooks/useCurrentBot';
import { useGroupedMessages } from '~hooks/useGroupedMessages';
import { useModal } from '~hooks/useModal';
import useSmoothWheel from '~hooks/useSmoothWheel';
import { generationApi } from '~services/api/generation';
import { chatVideoGenerationActions } from '~store/chat-video-generation';
import type { AppDispatch } from '~store/index';
import { fadeIn } from '~theme/snippets';
import { getMessageType } from '~utils/messages';
import { VideoFromImageModal } from '~widgets/Chat/ui/VideoFromImageModal';

import { messageSizeConfig } from '../../config';
import type { TMessage } from '../../model/types';
import MessageItem from '../MessageItemWithErrorBoundary';
import {
	MessageGroupTitle,
	MessageListContainer,
	MessageListInner,
	MessageListItem,
} from './styles';

type TMessageListProps = {
	onScrollTop: (isTopReached: boolean) => void;
	messages: TMessage[];
	userId: string;
	recipientId: string;
	hasMoreMessages: boolean;
	isLoading: boolean;
};

const findLastBotMessageBeforeIndex = (
	messages: TMessage[],
	index: number,
	botId: string
): TMessage | undefined => {
	index = Math.min(index, messages.length - 1);

	while (--index >= 0) {
		const message = messages[index];
		if (message && message.sender === botId) {
			return message;
		}
	}
};

export const MessageListV2: FC<TMessageListProps> = ({
	messages,
	hasMoreMessages,
	userId,
	recipientId,
	isLoading,
	onScrollTop,
}) => {
	const parentRef = useRef<HTMLDivElement | null>(null);
	const { bot } = useCurrentBot({ recipientId });
	const { data: generationQuota } =
		generationModel.useGetGenerationVideosQuota();
	const { pricePerGenerationInChat } = generationQuota;
	const { openPaywall } = usePaywallContext();
	const { balance, hasSubscription } = useAccountContext();
	// may be needed in future
	const [, setIsAtBottom] = useState(true);
	const [, setIsAtTop] = useState(false);
	const [animateMessageId, setAnimateMessageId] = useState<number | undefined>(
		undefined
	);
	const {
		open: openAnimateModal,
		isOpen: isAnimateModalOpen,
		close: closeAnimateModal,
	} = useModal();
	const [generate, { isLoading: isGenerationInProgress }] =
		generationApi.useGenerateVideoFromMessageImageMutation();
	const dispatch = useDispatch<AppDispatch>();
	const sendGenerateImagesAnimate = annals.generator.useGenerateImagesAnimate();
	const sendGenerateVideoSubmittedAnnals =
		annals.generator.useGenerateVideoSubmitted({
			free: false,
			source: 'chat_image_to_video',
		});

	const { groupNames, mergedMessages } = useGroupedMessages(messages);
	const activeStickyIndexRef = useRef<number | null>(null);

	const isActiveSticky = (index: number) =>
		activeStickyIndexRef.current === index;

	const estimateSize = useCallback(
		(index: number) => {
			const type = getMessageType(
				mergedMessages[index]?.meta ?? ({} as TMessage['meta'])
			);
			return messageSizeConfig[type] ?? 100;
		},
		[mergedMessages]
	);

	const count = mergedMessages.length;

	const transformIndex = useCallback(
		(index: number) => count - 1 - index,
		[count]
	);

	const stickyIndexes = useMemo(() => {
		const indexMap = mergedMessages.reduce((acc, item, index) => {
			if (!acc[item.tag]) {
				acc[item.tag] = index;
			}
			return acc;
		}, {} as Record<string, number>);

		return groupNames.map((gn) => transformIndex(indexMap[gn] ?? -1));
	}, [groupNames, mergedMessages, transformIndex]);

	const getItemKey = useCallback(
		(index: number) => mergedMessages[transformIndex(index)]?.tag ?? '',
		[mergedMessages, transformIndex]
	);

	const virtualizer = useVirtualizer({
		count,
		getScrollElement: useCallback(() => parentRef.current, []),
		getItemKey,
		estimateSize,
		overscan: 10,
		paddingStart: 20,
		onChange: useCallback(() => {
			//since list is reversed - top = bottom and wise versa
			const { clientHeight, scrollTop, scrollHeight } = parentRef.current ?? {
				clientHeight: 0,
				scrollTop: 0,
				scrollHeight: 0,
			};
			const atEndThreshold = Math.min((scrollHeight - clientHeight) / 2, 100);
			const atTop = scrollTop < 100;
			const atBottom =
				scrollTop + clientHeight >= scrollHeight - atEndThreshold;
			setIsAtTop(atBottom);
			setIsAtBottom(atTop);
		}, []),
		rangeExtractor: useCallback(
			(range: Range) => {
				let closestStickyIndex = Math.min(...stickyIndexes);

				for (let i = stickyIndexes.length - 1; i >= 0; i--) {
					const index = stickyIndexes[i];
					if (range.endIndex <= index) {
						closestStickyIndex = index;
						break;
					}
				}

				activeStickyIndexRef.current = closestStickyIndex;

				const next = new Set([
					activeStickyIndexRef.current,
					...defaultRangeExtractor(range),
				]);

				return [...next].sort((a, b) => a - b);
			},
			[stickyIndexes]
		),
	});

	const onProcessStart = useCallback(() => {
		parentRef.current?.scrollIntoView({ behavior: 'smooth' });
	}, []);

	const handleGenerateVideo = useCallback(
		(messageId: number) => {
			dispatch(chatVideoGenerationActions.addMessageId(messageId));
			void generate({ userId, botId: recipientId, messageId })
				.unwrap()
				.then(() => {
					sendGenerateVideoSubmittedAnnals();
					onProcessStart();
				})
				.catch(() => {
					dispatch(chatVideoGenerationActions.deleteMessageId(messageId));
				})
				.finally(() => {
					closeAnimateModal();
				});
		},
		[
			closeAnimateModal,
			dispatch,
			generate,
			onProcessStart,
			recipientId,
			sendGenerateVideoSubmittedAnnals,
			userId,
		]
	);

	const handleAnimateDialog = useCallback(
		(messageId: number) => {
			if (!hasSubscription || balance < pricePerGenerationInChat) {
				return openPaywall({
					prefer: 'subscription',
					reason: PaymentReason.VideoFromImageGeneration,
					reasonType: PaymentReasonType.Chat,
					botClientId: recipientId,
					cost: pricePerGenerationInChat,
				})();
			}

			const isModalHidden = loadFromSessionStorage(
				ssKeys.CHAT_VIDEO_GEN_MODAL_HIDDEN(userId),
				false
			);
			if (isModalHidden) {
				handleGenerateVideo(messageId);
			} else {
				setAnimateMessageId(messageId);
				openAnimateModal({ cb: () => setAnimateMessageId(undefined) })();
			}
			sendGenerateImagesAnimate({ source: 'chat' });
		},
		[
			balance,
			handleGenerateVideo,
			hasSubscription,
			openAnimateModal,
			openPaywall,
			pricePerGenerationInChat,
			recipientId,
			sendGenerateImagesAnimate,
			userId,
		]
	);

	const items = virtualizer.getVirtualItems();
	// check last item is in viewport => run callback
	// sticky elem will always have greater index, remove it from arr
	useEffect(() => {
		const nonStickyItems = items.filter(
			(item) => !stickyIndexes.includes(item.index)
		);
		const lastItem = nonStickyItems.pop();

		if (!lastItem) {
			return;
		}
		if (
			hasMoreMessages &&
			!isLoading &&
			lastItem.index + 1 >= messages.length
		) {
			onScrollTop(true);
		}
	}, [
		isLoading,
		items,
		messages.length,
		onScrollTop,
		stickyIndexes,
		hasMoreMessages,
	]);

	// since list is reversed we need to reverse mouse scroll direction
	// bad news wheel event sucks. so we're trying to animate reversed scroll, so it will look a bit more native
	useSmoothWheel(parentRef, 60, 5);

	if (!bot.clientId) {
		return undefined;
	}
	return (
		<MessageListContainer
			ref={parentRef}
			currentlyGenerating={bot.generations?.length > 0}
		>
			<MessageListInner
				className={fadeIn}
				style={{
					height: virtualizer.getTotalSize(),
				}}
			>
				{items.map((item) => {
					const transformedIndex = transformIndex(item.index);
					const message = mergedMessages[transformedIndex];
					const topicId = message?.meta?.automation?.topicId;
					const prevTopicId = findLastBotMessageBeforeIndex(
						mergedMessages,
						transformedIndex,
						recipientId
					)?.meta?.automation?.topicId;
					const isLast = item.index === 0;
					const showTopic = !!topicId && topicId !== prevTopicId;

					return (
						<MessageListItem
							key={item.key}
							data-index={item.index}
							ref={virtualizer.measureElement}
							style={{
								left: 0,
								width: '100%',
								...(isActiveSticky(item.index)
									? {
											position: 'sticky',
											top: 'auto',
											bottom: 0,
											transform: `scaleY(-1)`,
											alignSelf: 'flex-end',
											pointerEvents: 'none',
									  }
									: {
											top: 0,
											position: 'absolute',
											transform: `translateY(${item.start}px) scaleY(-1)`,
									  }),
							}}
						>
							{message?.tag === message?.id.toString() &&
							typeof message?.tag === 'string' ? (
								<MessageGroupTitle key={message?.tag}>
									<span>{message.id}</span>
								</MessageGroupTitle>
							) : (
								<MessageItem
									key={message?.tag}
									message={message}
									userId={userId}
									bot={bot}
									showTopic={showTopic}
									isLast={isLast}
									onAnimate={handleAnimateDialog}
								/>
							)}
						</MessageListItem>
					);
				})}
			</MessageListInner>

			<VideoFromImageModal
				open={isAnimateModalOpen}
				locked={true}
				messageId={animateMessageId}
				isInProgress={isGenerationInProgress}
				pricePerGeneration={pricePerGenerationInChat}
				handleGenerateVideo={handleGenerateVideo}
				onClose={closeAnimateModal}
			/>
		</MessageListContainer>
	);
};
